import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../services/api';
import './CustomerPublicInfo.css';

interface CustomerPublicInfoProps {
  customerId: number;
  customerName: string;
  customerType: string;
  onUpdate: () => void;
}

// For single-location partners (legacy Customer-based data)
interface SingleLocationData {
  public_store_name: string;
  public_address: string;
  public_city: string;
  public_state: string;
  public_zip: string;
  public_phone: string;
  public_email: string;
  public_website: string;
  public_hours: string;
  public_description: string;
  bridge_club_enabled: boolean;
  carries_red_bridge: boolean;
  carries_tiny_dinosaur: boolean;
  carries_poultry_layer: boolean;
  carries_poultry_meat: boolean;
  carries_goat: boolean;
  carries_sheep: boolean;
  carries_cattle: boolean;
  carries_swine: boolean;
  carries_rabbit: boolean;
  carries_dog: boolean;
  carries_cat: boolean;
  carries_horse: boolean;
  carries_game_bird: boolean;
}

// For multi-location partners (LocationPublicInfo-based data)
interface LocationPublicInfoData {
  id: number;
  location_id: number;
  show_in_store_finder: boolean;
  public_name: string;
  public_address: string;
  public_city: string;
  public_state: string;
  public_zip: string;
  public_phone: string;
  public_email: string;
  public_website: string;
  public_hours: string;
  public_description: string;
  carries_red_bridge: boolean;
  carries_tiny_dinosaur: boolean;
  carries_poultry_layer: boolean;
  carries_poultry_meat: boolean;
  carries_goat: boolean;
  carries_sheep: boolean;
  carries_cattle: boolean;
  carries_swine: boolean;
  carries_rabbit: boolean;
  carries_dog: boolean;
  carries_cat: boolean;
  carries_horse: boolean;
  carries_game_bird: boolean;
  latitude: number | null;
  longitude: number | null;
  logo_url: string | null;
}

interface LocationWithPublicInfo {
  location_id: number;
  location_name: string;
  is_primary: boolean;
  public_info: LocationPublicInfoData;
}

const CustomerPublicInfo: React.FC<CustomerPublicInfoProps> = ({
  customerId,
  customerName,
  customerType,
  onUpdate,
}) => {
  // Multi-location state
  const [isMultiLocation, setIsMultiLocation] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [locations, setLocations] = useState<LocationWithPublicInfo[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);

  // Current location's public info (for multi-location)
  const [locationData, setLocationData] = useState<LocationPublicInfoData | null>(null);

  // Single-location state (legacy)
  const [singleFormData, setSingleFormData] = useState<SingleLocationData>({
    public_store_name: '',
    public_address: '',
    public_city: '',
    public_state: '',
    public_zip: '',
    public_phone: '',
    public_email: '',
    public_website: '',
    public_hours: '',
    public_description: '',
    bridge_club_enabled: false,
    carries_red_bridge: true,
    carries_tiny_dinosaur: false,
    carries_poultry_layer: false,
    carries_poultry_meat: false,
    carries_goat: false,
    carries_sheep: false,
    carries_cattle: false,
    carries_swine: false,
    carries_rabbit: false,
    carries_dog: false,
    carries_cat: false,
    carries_horse: false,
    carries_game_bird: false,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const isRetailPartner = customerType === 'RETAIL_PARTNER';

  const usStates = [
    '', 'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  useEffect(() => {
    loadPublicInfo();
  }, [customerId]);

  const loadPublicInfo = async () => {
    try {
      setLoading(true);
      setError(null);

      // First, check if this is a multi-location partner
      const multiLocResponse = await fetch(
        `${API_BASE_URL}/admin/customers/${customerId}/location-public-info/`,
        { credentials: 'include' }
      );

      if (multiLocResponse.ok) {
        const multiLocData = await multiLocResponse.json();

        if (multiLocData.is_multi_location && multiLocData.locations.length > 0) {
          // Multi-location partner
          setIsMultiLocation(true);
          setOrgName(multiLocData.org_name);
          setLocations(multiLocData.locations);

          // Select the primary location by default, or the first one
          const primaryLoc = multiLocData.locations.find((l: LocationWithPublicInfo) => l.is_primary);
          const firstLoc = primaryLoc || multiLocData.locations[0];
          setSelectedLocationId(firstLoc.location_id);
          setLocationData(firstLoc.public_info);
          setHasChanges(false);
          return;
        }
      }

      // Single-location partner - load from Customer record
      setIsMultiLocation(false);
      const response = await fetch(`${API_BASE_URL}/customers/${customerId}/`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to load customer data');
      }
      const data = await response.json();
      setSingleFormData({
        public_store_name: data.public_store_name || '',
        public_address: data.public_address || '',
        public_city: data.public_city || '',
        public_state: data.public_state || '',
        public_zip: data.public_zip || '',
        public_phone: data.public_phone || '',
        public_email: data.public_email || '',
        public_website: data.public_website || '',
        public_hours: data.public_hours || '',
        public_description: data.public_description || '',
        bridge_club_enabled: data.bridge_club_enabled || false,
        carries_red_bridge: data.carries_red_bridge ?? true,
        carries_tiny_dinosaur: data.carries_tiny_dinosaur ?? false,
        carries_poultry_layer: data.carries_poultry_layer ?? false,
        carries_poultry_meat: data.carries_poultry_meat ?? false,
        carries_goat: data.carries_goat ?? false,
        carries_sheep: data.carries_sheep ?? false,
        carries_cattle: data.carries_cattle ?? false,
        carries_swine: data.carries_swine ?? false,
        carries_rabbit: data.carries_rabbit ?? false,
        carries_dog: data.carries_dog ?? false,
        carries_cat: data.carries_cat ?? false,
        carries_horse: data.carries_horse ?? false,
        carries_game_bird: data.carries_game_bird ?? false,
      });
      setHasChanges(false);
    } catch (err: any) {
      setError(err.message || 'Failed to load public info');
    } finally {
      setLoading(false);
    }
  };

  const handleLocationSelect = (locationId: number) => {
    if (hasChanges) {
      if (!window.confirm('You have unsaved changes. Discard them?')) {
        return;
      }
    }
    setSelectedLocationId(locationId);
    const loc = locations.find(l => l.location_id === locationId);
    if (loc) {
      setLocationData({ ...loc.public_info });
    }
    setHasChanges(false);
    setSuccess(null);
    setError(null);
  };

  const handleMultiLocationChange = (field: keyof LocationPublicInfoData, value: string | boolean) => {
    if (!locationData) return;
    setLocationData(prev => prev ? { ...prev, [field]: value } : prev);
    setHasChanges(true);
    setSuccess(null);
  };

  const handleSingleChange = (field: keyof SingleLocationData, value: string | boolean) => {
    setSingleFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
    setSuccess(null);
  };

  const handleSyncFromLocation = async () => {
    if (!selectedLocationId) return;

    try {
      setSyncing(true);
      setError(null);

      const response = await fetch(
        `${API_BASE_URL}/admin/location-public-info/${selectedLocationId}/sync/`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ force: true }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to sync');
      }

      const data = await response.json();
      setLocationData(data.public_info);
      setSuccess('Synced address from Locations tab');
      setHasChanges(true);
    } catch (err: any) {
      setError(err.message || 'Failed to sync from location');
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveMultiLocation = async () => {
    if (!selectedLocationId || !locationData) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await fetch(
        `${API_BASE_URL}/admin/location-public-info/${selectedLocationId}/`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(locationData),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || data.error || 'Failed to save');
      }

      const data = await response.json();

      // Update the locations list with the new data
      setLocations(prev => prev.map(loc =>
        loc.location_id === selectedLocationId
          ? { ...loc, public_info: data.public_info }
          : loc
      ));
      setLocationData(data.public_info);

      setSuccess('Public info saved successfully!');
      setHasChanges(false);
      onUpdate();
    } catch (err: any) {
      setError(err.message || 'Failed to save public info');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSingleLocation = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await fetch(`${API_BASE_URL}/customers/${customerId}/`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(singleFormData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || data.error || 'Failed to save');
      }

      setSuccess('Public info saved successfully!');
      setHasChanges(false);
      onUpdate();
    } catch (err: any) {
      setError(err.message || 'Failed to save public info');
    } finally {
      setSaving(false);
    }
  };

  if (!isRetailPartner) {
    return (
      <div className="public-info-section">
        <div className="public-info-unavailable">
          <h3>Public Store Info</h3>
          <p>Public-facing store information for Bridge Club is only available for Retail Partner customers.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="public-info-section">
        <div className="public-info-loading">Loading...</div>
      </div>
    );
  }

  // ==========================================================================
  // MULTI-LOCATION RENDER
  // ==========================================================================
  if (isMultiLocation && locationData) {
    const selectedLocation = locations.find(l => l.location_id === selectedLocationId);

    return (
      <div className="public-info-section">
        <div className="public-info-header">
          <h3>Public Store Info</h3>
          <p className="public-info-description">
            {orgName} has {locations.length} location{locations.length !== 1 ? 's' : ''}.
            Select a location to edit its public store finder info.
          </p>
        </div>

        {/* Location Selector */}
        <div className="location-selector">
          <div className="location-tabs">
            {locations.map(loc => (
              <button
                key={loc.location_id}
                className={`location-tab ${loc.location_id === selectedLocationId ? 'active' : ''}`}
                onClick={() => handleLocationSelect(loc.location_id)}
              >
                {loc.location_name}
                {loc.is_primary && <span className="primary-badge">Primary</span>}
                {loc.public_info.show_in_store_finder && (
                  <span className="visible-badge" title="Visible in Store Finder">&#10003;</span>
                )}
              </button>
            ))}
          </div>
          <button
            className="btn btn-secondary btn-sync"
            onClick={handleSyncFromLocation}
            disabled={syncing}
            title="Pull address from Locations tab"
          >
            {syncing ? 'Syncing...' : 'Sync from Locations'}
          </button>
        </div>

        {error && <div className="public-info-error">{error}</div>}
        {success && <div className="public-info-success">{success}</div>}

        <div className="public-info-form">
          {/* Store Finder Toggle */}
          <div className="form-section bridge-club-toggle-section">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={locationData.show_in_store_finder}
                onChange={(e) => handleMultiLocationChange('show_in_store_finder', e.target.checked)}
                className="toggle-checkbox"
              />
              <span className="toggle-switch"></span>
              <span className="toggle-text">
                <strong>Show in Bridge Club Store Finder</strong>
                <small>When enabled, this location appears in the public store finder</small>
              </span>
            </label>
          </div>

          {/* Brands Carried */}
          <div className="form-section">
            <h4>Brands Carried</h4>
            <p className="form-section-hint">Select which feed brands this location carries</p>
            <div className="checkbox-grid checkbox-grid-2">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={locationData.carries_red_bridge}
                  onChange={(e) => handleMultiLocationChange('carries_red_bridge', e.target.checked)}
                />
                <span className="checkbox-text">Red Bridge Farm Feed Co.</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={locationData.carries_tiny_dinosaur}
                  onChange={(e) => handleMultiLocationChange('carries_tiny_dinosaur', e.target.checked)}
                />
                <span className="checkbox-text">Tiny Dinosaur Feed</span>
              </label>
            </div>
          </div>

          {/* Animal Types */}
          <div className="form-section">
            <h4>Animal Types In Stock</h4>
            <p className="form-section-hint">Select which animal feed types this location typically carries</p>
            <div className="checkbox-grid checkbox-grid-2">
              {[
                { field: 'carries_poultry_layer', label: 'Poultry - Layers' },
                { field: 'carries_poultry_meat', label: 'Poultry - Meat Birds' },
                { field: 'carries_goat', label: 'Goat' },
                { field: 'carries_sheep', label: 'Sheep' },
                { field: 'carries_cattle', label: 'Cattle' },
                { field: 'carries_swine', label: 'Swine' },
                { field: 'carries_rabbit', label: 'Rabbit' },
                { field: 'carries_dog', label: 'Dog' },
                { field: 'carries_cat', label: 'Cat' },
                { field: 'carries_horse', label: 'Horse' },
                { field: 'carries_game_bird', label: 'Game Bird' },
              ].map(({ field, label }) => (
                <label key={field} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={locationData[field as keyof LocationPublicInfoData] as boolean}
                    onChange={(e) => handleMultiLocationChange(field as keyof LocationPublicInfoData, e.target.checked)}
                  />
                  <span className="checkbox-text">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Store Name */}
          <div className="form-section">
            <h4>Store Display Name</h4>
            <div className="form-group">
              <label htmlFor="public_name">Public Store Name</label>
              <input
                type="text"
                id="public_name"
                value={locationData.public_name}
                onChange={(e) => handleMultiLocationChange('public_name', e.target.value)}
                placeholder={selectedLocation?.location_name || 'Store Name'}
                className="form-input"
              />
            </div>
          </div>

          {/* Address */}
          <div className="form-section">
            <h4>Public Address</h4>
            <div className="form-group">
              <label htmlFor="public_address">Street Address</label>
              <input
                type="text"
                id="public_address"
                value={locationData.public_address}
                onChange={(e) => handleMultiLocationChange('public_address', e.target.value)}
                placeholder="123 Main Street"
                className="form-input"
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="public_city">City</label>
                <input
                  type="text"
                  id="public_city"
                  value={locationData.public_city}
                  onChange={(e) => handleMultiLocationChange('public_city', e.target.value)}
                  placeholder="City"
                  className="form-input"
                />
              </div>
              <div className="form-group form-group-small">
                <label htmlFor="public_state">State</label>
                <select
                  id="public_state"
                  value={locationData.public_state}
                  onChange={(e) => handleMultiLocationChange('public_state', e.target.value)}
                  className="form-input"
                >
                  {usStates.map((st) => (
                    <option key={st} value={st}>{st || 'Select...'}</option>
                  ))}
                </select>
              </div>
              <div className="form-group form-group-small">
                <label htmlFor="public_zip">ZIP</label>
                <input
                  type="text"
                  id="public_zip"
                  value={locationData.public_zip}
                  onChange={(e) => handleMultiLocationChange('public_zip', e.target.value)}
                  placeholder="99999"
                  className="form-input"
                  maxLength={10}
                />
              </div>
            </div>
            {locationData.latitude && locationData.longitude && (
              <div className="coordinates-display">
                Coordinates: {locationData.latitude.toFixed(6)}, {locationData.longitude.toFixed(6)}
              </div>
            )}
          </div>

          {/* Contact Info */}
          <div className="form-section">
            <h4>Public Contact Info</h4>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="public_phone">Public Phone</label>
                <input
                  type="tel"
                  id="public_phone"
                  value={locationData.public_phone}
                  onChange={(e) => handleMultiLocationChange('public_phone', e.target.value)}
                  placeholder="(509) 555-1234"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="public_email">Public Email</label>
                <input
                  type="email"
                  id="public_email"
                  value={locationData.public_email}
                  onChange={(e) => handleMultiLocationChange('public_email', e.target.value)}
                  placeholder="info@store.com"
                  className="form-input"
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="public_website">Website</label>
              <input
                type="text"
                id="public_website"
                value={locationData.public_website}
                onChange={(e) => handleMultiLocationChange('public_website', e.target.value)}
                placeholder="https://www.yourstore.com"
                className="form-input"
              />
            </div>
          </div>

          {/* Hours & Description */}
          <div className="form-section">
            <h4>Store Hours & Description</h4>
            <div className="form-group">
              <label htmlFor="public_hours">Store Hours</label>
              <textarea
                id="public_hours"
                value={locationData.public_hours}
                onChange={(e) => handleMultiLocationChange('public_hours', e.target.value)}
                placeholder="Mon-Fri: 8am-6pm&#10;Sat: 9am-5pm&#10;Sun: Closed"
                className="form-input form-textarea"
                rows={3}
              />
            </div>
            <div className="form-group">
              <label htmlFor="public_description">Store Description</label>
              <textarea
                id="public_description"
                value={locationData.public_description}
                onChange={(e) => handleMultiLocationChange('public_description', e.target.value)}
                placeholder="A brief description shown to Bridge Club members..."
                className="form-input form-textarea"
                rows={3}
                maxLength={1000}
              />
              <span className="form-hint">{locationData.public_description.length}/1000 characters</span>
            </div>
          </div>

          {/* Save Button */}
          <div className="form-actions">
            <button
              className="btn btn-primary"
              onClick={handleSaveMultiLocation}
              disabled={saving || !hasChanges}
            >
              {saving ? 'Saving...' : 'Save Public Info'}
            </button>
            {hasChanges && (
              <span className="unsaved-indicator">You have unsaved changes</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ==========================================================================
  // SINGLE-LOCATION RENDER (Legacy)
  // ==========================================================================
  return (
    <div className="public-info-section">
      <div className="public-info-header">
        <h3>Public Store Info</h3>
        <p className="public-info-description">
          This information is shown to Bridge Club members in the store finder.
          Leave fields blank to use your company's default contact info (except phone - left blank for privacy).
        </p>
      </div>

      {error && <div className="public-info-error">{error}</div>}
      {success && <div className="public-info-success">{success}</div>}

      <div className="public-info-form">
        {/* Bridge Club Enabled Toggle */}
        <div className="form-section bridge-club-toggle-section">
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={singleFormData.bridge_club_enabled}
              onChange={(e) => handleSingleChange('bridge_club_enabled', e.target.checked)}
              className="toggle-checkbox"
            />
            <span className="toggle-switch"></span>
            <span className="toggle-text">
              <strong>Show in Bridge Club Store Finder</strong>
              <small>When enabled, this store appears in the Bridge Club app for members to find</small>
            </span>
          </label>
        </div>

        {/* Brands Carried */}
        <div className="form-section">
          <h4>Brands Carried</h4>
          <p className="form-section-hint">Select which feed brands this store carries</p>
          <div className="checkbox-grid checkbox-grid-2">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={singleFormData.carries_red_bridge}
                onChange={(e) => handleSingleChange('carries_red_bridge', e.target.checked)}
              />
              <span className="checkbox-text">Red Bridge Farm Feed Co.</span>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={singleFormData.carries_tiny_dinosaur}
                onChange={(e) => handleSingleChange('carries_tiny_dinosaur', e.target.checked)}
              />
              <span className="checkbox-text">Tiny Dinosaur Feed</span>
            </label>
          </div>
        </div>

        {/* Animal Types Carried */}
        <div className="form-section">
          <h4>Animal Types In Stock</h4>
          <p className="form-section-hint">Select which animal feed types this store typically carries</p>
          <div className="checkbox-grid checkbox-grid-2">
            {[
              { field: 'carries_poultry_layer', label: 'Poultry - Layers' },
              { field: 'carries_poultry_meat', label: 'Poultry - Meat Birds' },
              { field: 'carries_goat', label: 'Goat' },
              { field: 'carries_sheep', label: 'Sheep' },
              { field: 'carries_cattle', label: 'Cattle' },
              { field: 'carries_swine', label: 'Swine' },
              { field: 'carries_rabbit', label: 'Rabbit' },
              { field: 'carries_dog', label: 'Dog' },
              { field: 'carries_cat', label: 'Cat' },
              { field: 'carries_horse', label: 'Horse' },
              { field: 'carries_game_bird', label: 'Game Bird' },
            ].map(({ field, label }) => (
              <label key={field} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={singleFormData[field as keyof SingleLocationData] as boolean}
                  onChange={(e) => handleSingleChange(field as keyof SingleLocationData, e.target.checked)}
                />
                <span className="checkbox-text">{label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Store Name */}
        <div className="form-section">
          <h4>Store Display Name</h4>
          <div className="form-group">
            <label htmlFor="public_store_name">Public Store Name</label>
            <input
              type="text"
              id="public_store_name"
              value={singleFormData.public_store_name}
              onChange={(e) => handleSingleChange('public_store_name', e.target.value)}
              placeholder={customerName}
              className="form-input"
            />
            <span className="form-hint">Leave blank to use "{customerName}"</span>
          </div>
        </div>

        {/* Address */}
        <div className="form-section">
          <h4>Public Address</h4>
          <div className="form-group">
            <label htmlFor="public_address">Street Address</label>
            <input
              type="text"
              id="public_address"
              value={singleFormData.public_address}
              onChange={(e) => handleSingleChange('public_address', e.target.value)}
              placeholder="123 Main Street"
              className="form-input"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="public_city">City</label>
              <input
                type="text"
                id="public_city"
                value={singleFormData.public_city}
                onChange={(e) => handleSingleChange('public_city', e.target.value)}
                placeholder="City"
                className="form-input"
              />
            </div>
            <div className="form-group form-group-small">
              <label htmlFor="public_state">State</label>
              <select
                id="public_state"
                value={singleFormData.public_state}
                onChange={(e) => handleSingleChange('public_state', e.target.value)}
                className="form-input"
              >
                {usStates.map((st) => (
                  <option key={st} value={st}>{st || 'Select...'}</option>
                ))}
              </select>
            </div>
            <div className="form-group form-group-small">
              <label htmlFor="public_zip">ZIP</label>
              <input
                type="text"
                id="public_zip"
                value={singleFormData.public_zip}
                onChange={(e) => handleSingleChange('public_zip', e.target.value)}
                placeholder="99999"
                className="form-input"
                maxLength={10}
              />
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="form-section">
          <h4>Public Contact Info</h4>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="public_phone">Public Phone</label>
              <input
                type="tel"
                id="public_phone"
                value={singleFormData.public_phone}
                onChange={(e) => handleSingleChange('public_phone', e.target.value)}
                placeholder="(509) 555-1234"
                className="form-input"
              />
              <span className="form-hint">Store main line, not manager's cell</span>
            </div>
            <div className="form-group">
              <label htmlFor="public_email">Public Email</label>
              <input
                type="email"
                id="public_email"
                value={singleFormData.public_email}
                onChange={(e) => handleSingleChange('public_email', e.target.value)}
                placeholder="info@store.com"
                className="form-input"
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="public_website">Website</label>
            <input
              type="text"
              id="public_website"
              value={singleFormData.public_website}
              onChange={(e) => handleSingleChange('public_website', e.target.value)}
              placeholder="https://www.yourstore.com"
              className="form-input"
            />
          </div>
        </div>

        {/* Hours & Description */}
        <div className="form-section">
          <h4>Store Hours & Description</h4>
          <div className="form-group">
            <label htmlFor="public_hours">Store Hours</label>
            <textarea
              id="public_hours"
              value={singleFormData.public_hours}
              onChange={(e) => handleSingleChange('public_hours', e.target.value)}
              placeholder="Mon-Fri: 8am-6pm&#10;Sat: 9am-5pm&#10;Sun: Closed"
              className="form-input form-textarea"
              rows={3}
            />
          </div>
          <div className="form-group">
            <label htmlFor="public_description">Store Description</label>
            <textarea
              id="public_description"
              value={singleFormData.public_description}
              onChange={(e) => handleSingleChange('public_description', e.target.value)}
              placeholder="A brief description shown to Bridge Club members..."
              className="form-input form-textarea"
              rows={3}
              maxLength={500}
            />
            <span className="form-hint">{singleFormData.public_description.length}/500 characters</span>
          </div>
        </div>

        {/* Save Button */}
        <div className="form-actions">
          <button
            className="btn btn-primary"
            onClick={handleSaveSingleLocation}
            disabled={saving || !hasChanges}
          >
            {saving ? 'Saving...' : 'Save Public Info'}
          </button>
          {hasChanges && (
            <span className="unsaved-indicator">You have unsaved changes</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerPublicInfo;
